import { OfferSubCategory } from '../../../common/enums.js';
import { t } from '../../../common/i18n/index.js';
import { eurosPerMonthCapMsg } from '../../../common/i18n/shared.js';
import { formatPhoneNumber } from '../../../common/utils/phoneNumberUtils.js';
import { eurosToCents, formatSumToString } from '../../../common/utils/priceUtils.js';
import { ContactMedium } from '../../../generated/api/contactMedium.js';
import { getProductAvailability } from '../../DeviceDetailsCoCo/deviceDetailsUtilsCoCo.js';
import type { ProductOfferingCartItems } from '../../../common/types/shoppingBasketCoCo.js';
import type { MediumCharacteristic } from '../../../generated/api/mediumCharacteristic.js';
import type { RelatedParty } from '../../../generated/api/relatedParty.js';
import type { OfferingWithStock, ProductAvailability } from '../../DeviceDetailsCoCo/deviceDetailsUtilsCoCo.js';
import type { PlaceOrderFormData, PlaceOrderItemUnit } from '../placeOrderTypes.js';
import type { UseFormReturn } from 'react-hook-form';

/**
 * A basket item represents an EPP (Elisa Palvelupäätelaite) device when either the catalogue marks it
 * as an EPP offer subcategory, or an EPP installment term was selected for it in the device details view
 * (see createDeviceCartItem/buildSelectedDevicePrice in deviceDetailsUtilsCoCo).
 */
export const isEppDeviceBasketItem = (basketItem: ProductOfferingCartItems): boolean =>
  basketItem.productOfferingType?.subcategory === OfferSubCategory.EPP_DEVICE_POT ||
  (basketItem.productOfferingPrice ?? []).some(price => (price.installmentTerm ?? []).length > 0);

export const getProductOfferAddOnPrices = (addOnPriceInEuros: number) => ({
  value: `${formatSumToString(eurosToCents(addOnPriceInEuros), true)} ${t.H37G(eurosPerMonthCapMsg)}`,
});

const getMediumValue = (
  contactMedium: ContactMedium[] | undefined,
  mediumType: ContactMedium.MediumTypeEnum,
  pick: (characteristic: MediumCharacteristic) => string | undefined
): string => {
  const medium = contactMedium?.find(cm => cm.mediumType === mediumType);
  return (medium && pick(medium.characteristic)) ?? '';
};

export const getRelatedPartyForCustomerStep = (
  relatedParties: RelatedParty[] | undefined,
  contactMasterId: string | undefined
): string[] => {
  const relatedParty = contactMasterId ? relatedParties?.find(party => party.id === contactMasterId) : undefined;

  if (!relatedParty) {
    return [];
  }

  const name = relatedParty.fullName ?? '';
  const email = getMediumValue(relatedParty.contactMedium, ContactMedium.MediumTypeEnum.email, c => c.emailAddress);
  const phoneNumber = getMediumValue(
    relatedParty.contactMedium,
    ContactMedium.MediumTypeEnum.telephone,
    c => c.phoneNumber
  );
  const phone = phoneNumber ? formatPhoneNumber(phoneNumber) : '';

  return [name, phone, email].filter((v): v is string => v.trim().length > 0);
};

export const getBasketItemAvailability = (
  offers: Array<{ id: string } & OfferingWithStock>,
  offerId: string
): ProductAvailability | null => {
  const availability = getProductAvailability(offers.find(offer => offer.id === offerId));
  return availability?.color ? availability : null;
};

export const resetCoCoMainFormOrderItemUnits = (
  methods: UseFormReturn<PlaceOrderFormData>,
  orderItemUnits: PlaceOrderItemUnit[]
) => {
  const mainFormValues = methods.getValues();

  methods.reset(
    {
      ...mainFormValues,
      products: {
        ...mainFormValues.products,
        placeOrderItemUnits: orderItemUnits,
      },
    },
    { keepDefaultValues: true }
  );
};
